/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { SearchError } from '../search-manager';
import LibraryMosaicService from '../LibraryMosaicService';
import { getLogger } from '../../utils/SearchLogger';
import { LIBRARY_MIMETYPE_MAP } from '../../containers/SearchResultsPage/SearchConstants';
import SearchUtils from '../../utils/SearchUtils';
import USSRequestBody from './USSRequestBody';

const sortMapper = {
	Ascending: {
		Name: 'name',
		ModificationDate: 'modified_date',
		Size: 'size',
		CreationDate: '', // added to shut up flow only
	},
	Descending: {
		Name: '-name',
		ModificationDate: '-modified_date',
		Size: '-size',
		CreationDate: '', // added to shut up flow only
	},
};
const ownershipMapper = {
	private: 'owner',
	incoming: 'sharedWithMe',
	outgoing: 'sharedByMe',
};
const SharingMapper = {
	owner: 'owner',
	sharedWithMe: 'shared',
	sharedByMe: 'shared',
	sharedWithYou: 'shared', // not being used
	orgShared: 'team',
};
export class LibrarySearch implements Searchable {
	static VIDEO_RENDITION_SIZE: number;
	static VIDEO_SIZE_SLOP: number;
	static IMAGE_RENDITION_SIZE: number;
	searchParams: Record<string, any> = {};
	hostContext: HostContext;
	hasValidParams = false;
	name: string;
	logger: Logger;

	constructor(hostContext: HostContext, params: Record<string, any>) {
		this.searchParams = params;
		this.hostContext = hostContext;
		this.name = 'LocalLibrarySearch';
		this.logger = getLogger();
		this.hasValidParams =
			params.serviceHost &&
			params.serviceHost.length > 0 &&
			params.serviceToken &&
			params.serviceToken.length > 0;
	}

	async search(query: SearchablesQuery, startIndex: StartIndexType, limit: number): Promise<SearchResult> {
		let promise;
		const librarySelectedInTypOrSource = this.isLibrarySelectedInTypOrSource(query.filter);
		const librarySearchNotNeeded = !this.hasValidParams || !librarySelectedInTypOrSource;

		if (librarySearchNotNeeded) {
			// if we don't have correct params
			// just return an empty search
			promise = new Promise((resolve) => {
				resolve({
					resultItems: [],
					serverResult: {
						url: '',
						message: '',
						requestId: '0',
						status: 200,
						duration: 0,
					},
					totalResults: 0,
					duration: 0,
				});
			});
		} else {
			promise = new Promise(
				async (
					resolve,
					/* reject */
				) => {
					const startTime = Date.now();
					const libraryResults = await this._searchLibraries(query, startIndex, limit);
					const duration = Date.now() - startTime;
					// Save the timing data to session storage
					SearchUtils.saveSearchTimingInSessionStorage('localLibrarySearch', 'server', duration);

					if (libraryResults instanceof Error) {
						// pass along the error
						// FIXME: we should reject with a SearchError object
						// @ts-ignore
						libraryResults.duration = duration;
						resolve(libraryResults);
					} else {
						const results: SearchResult = this._convertToSearchResult(libraryResults);
						results.duration = duration;
						resolve(results);
					}
				},
			);
		}

		return promise;
	}

	_getOnlyLibraryElements(query: SearchablesQuery): boolean {
		let libraryElementInDocumentTypes = false;
		let libraryInDocumentTypes = false;
		if (query.filter.type && query.filter.type.length > 0) {
			libraryElementInDocumentTypes = query.filter.type.includes('LibraryElement');
			libraryInDocumentTypes = query.filter.type.includes('Library');
		}
		return libraryElementInDocumentTypes && !libraryInDocumentTypes;
	}

	_getOnlyLibraries(query: SearchablesQuery): boolean {
		let libraryElementInDocumentTypes = false;
		let libraryInDocumentTypes = false;
		if (query.filter.type && query.filter.type.length > 0) {
			libraryElementInDocumentTypes = query.filter.type.includes('LibraryElement');
			libraryInDocumentTypes = query.filter.type.includes('Library');
		}
		return !libraryElementInDocumentTypes && libraryInDocumentTypes;
	}

	__isSourceFilterLibraryOrLibraryElementSelected(query: SearchablesQuery): boolean[] {
		let teamLibrariesFiltered = false;
		let yourLibrariesFiltered = false;
		if (query.filter.source && query.filter.source.length > 0) {
			teamLibrariesFiltered = query.filter.source.includes('TeamLibraries');
			yourLibrariesFiltered = query.filter.source.includes('YourLibraries');
		}
		return [teamLibrariesFiltered, yourLibrariesFiltered];
	}

	_getOwnerFilters(query: SearchablesQuery): string {
		const [teamLibrariesFiltered, yourLibrariesFiltered] =
			this.__isSourceFilterLibraryOrLibraryElementSelected(query);

		const sharedWithYouSelected = query.filter.source && query.filter.source.includes('SharedWithYou');
		// Initialize it to owner and shared libraries
		const owner: Set<string> = new Set();

		teamLibrariesFiltered && owner.add('team');
		yourLibrariesFiltered && owner.add('owner');
		sharedWithYouSelected && owner.add('shared');

		return Array.from(owner).join(',');
	}

	/**
	 * Checks whether anything related to library is selected in filters
	 * TODO: Simplify this logic at the first chance
	 * @param filter
	 * @returns
	 */
	private isLibrarySelectedInTypOrSource(filter: SelectedUiFilters): boolean {
		const anyTypeSelected = filter.type && filter.type.length > 0;
		const anySourceSelected = filter.source && filter.source.length > 0;

		if (!anyTypeSelected && !anySourceSelected) {
			return true;
		}

		const libraryOrLibraryElementTypeSelected = anyTypeSelected
			? filter.type.includes('LibraryElement') || filter.type.includes('Library')
			: false;

		const yourOrTeamLibrariesSourceSelected = anySourceSelected
			? filter.source.includes('YourLibraries') ||
				filter.source.includes('TeamLibraries') ||
				filter.source.includes('SharedWithYou')
			: false;

		if (yourOrTeamLibrariesSourceSelected && libraryOrLibraryElementTypeSelected) {
			return true;
		}

		if (libraryOrLibraryElementTypeSelected && (!anySourceSelected || yourOrTeamLibrariesSourceSelected)) {
			return true;
		}

		if (yourOrTeamLibrariesSourceSelected && (!anyTypeSelected || libraryOrLibraryElementTypeSelected)) {
			return true;
		}

		return false;
	}

	// eslint-disable-next-line no-unused-vars
	_searchLibraries(query: SearchablesQuery, startIndex: StartIndexType, limit: number): Promise<Record<string, any>> {
		return new Promise((resolve) => {
			const libraryRequest = new XMLHttpRequest();
			const url = `${this.searchParams.serviceHost}/search`;
			libraryRequest.open('POST', url, true);
			libraryRequest.setRequestHeader('token', this.searchParams.serviceToken);
			libraryRequest.setRequestHeader('Content-Type', 'application/json');
			libraryRequest.responseType = 'json';
			libraryRequest.timeout = 4 * 1000; // four seconds

			const startTime = Date.now();

			libraryRequest.onload = () => {
				const serverResult = {
					url,
					message: libraryRequest.statusText,
					requestId: `${startTime}`,
					status: libraryRequest.status,
					duration: Date.now() - startTime,
				};

				if (libraryRequest.status === 200) {
					if (libraryRequest.getResponseHeader('Content-Type')) {
						const contentType = libraryRequest.getResponseHeader('Content-Type') as string;
						contentType.trim();

						if (contentType.startsWith('application/json')) {
							// we have a json response
							const searchResults = {
								response: libraryRequest.response,
								serverResult,
							};

							if (searchResults.response) {
								resolve(searchResults);
							} else {
								this.logger.error('Local Library Search', 'No response was returned');
								// FIXME: reject with SearchError
								serverResult.status = 204;
								serverResult.message = 'No response returned from local library search';
								resolve(new SearchError(serverResult));
							}
						} else {
							// FIXME: reject with SearchError
							serverResult.status = 415;
							serverResult.message = `Unsupported Content-Type from local library search ${contentType}, response=${libraryRequest.response}`;
							resolve(new SearchError(serverResult));
						}
					} else {
						// FIXME: reject with SearchError
						serverResult.status = 415;
						serverResult.message = 'No Content-Type header from local library search';
						resolve(new SearchError(serverResult));
					}
				} else {
					// FIXME: reject with SearchError
					resolve(new SearchError(serverResult));
				}
			};

			libraryRequest.onerror = () => {
				const serverResult = {
					url,
					message: 'request failed with error event, onerror',
					requestId: `${startTime}`,
					// return a 500, Internal Server Error if we can't connect to lib process
					status: 500,
					duration: Date.now() - startTime,
				};
				this.logger.error('Local Library Service', 'search request failed', serverResult);
				// FIXME: reject with SearchError
				resolve(new SearchError(serverResult));
			};

			libraryRequest.ontimeout = () => {
				const serverResult = {
					url,
					message: libraryRequest.statusText || 'Local library search timeout',
					requestId: `${startTime}`,
					status: 408,
					duration: Date.now() - startTime,
				};
				this.logger.error('Local Library Service', 'search request timed out', serverResult);
				// FIXME: reject with SearchError
				resolve(new SearchError(serverResult));
			};

			// see if we have a date range
			const dateRange: DateRange = USSRequestBody.processSelectedModifiedDateFilter(query.filter);
			const dateArray = dateRange ? [dateRange.min, dateRange.max] : undefined;

			const owner = this._getOwnerFilters(query);

			// leave libraryId undefined
			let libraryId;

			// if (query.filter && query.filter.inLibrary) {
			// 	// search the specified library
			// 	libraryId = query.filter.inLibrary.id;
			// }

			/**
			 * HACK: ['all'] in element_types makes process not return any of the library elements
			 * ? This is done because no better way of filtering out libraries only
			 * TODO: Remove this once there is proper filter item from Library process team to get Libraries only
			 **/
			// if the value is undefined, no property will be in the filter object
			let filters = {
				owner,
				// filter toolkits
				toolkit_id: false,
				// eslint-disable-line camelcase
				library_id: libraryId,
				// eslint-disable-line camelcase
				modified_date: dateArray,
				// eslint-disable-line camelcase
				elements_only: this._getOnlyLibraryElements(query),
				// eslint-disable-line camelcase
				element_types: this._getOnlyLibraries(query) ? ['all'] : [], // eslint-disable-line camelcase
			};
			const hasValues =
				Object.values(filters).reduce((acc, curr) => {
					let result = acc;

					if (curr !== undefined) {
						result = true;
					}

					return result;
				}, false) === true;

			if (!hasValues) {
				// @ts-ignore
				filters = undefined;
			}

			const start = startIndex === 0 ? undefined : startIndex;
			// set up the body of the POST request
			const body = {
				q: query.query,
				filters,
				// eslint-disable-next-line camelcase
				order_by: sortMapper[query.sort.sortOrder][query.sort.sortBy],
				start,
				limit,
				// eslint-disable-next-line camelcase
				thumbnail_cache: true,
			};
			// send it
			libraryRequest.send(JSON.stringify(body));
		});
	}

	_isTeamAsset(libResult: Record<string, any>) {
		let isTeamAsset = false;

		if (libResult.team_id) {
			isTeamAsset = true;
		}

		if (libResult.library && libResult.library.collaboration && libResult.library.collaboration.role !== 'owner') {
			isTeamAsset = true;
		}

		return isTeamAsset;
	}

	_convertToSearchResult(libraryResults: Record<string, any>): SearchResult {
		const libraryMosaicService = new LibraryMosaicService(this.hostContext);
		const resultItems: Array<ResultItem> = libraryResults.response.results.map((libResult) => {
			// check to see if it has a library property
			// library elements have a library property, libraries don't
			const isLibrary = !libResult.library;
			// leave color theme data undefined
			let data;

			if (libResult.type === 'application/vnd.adobe.element.colortheme+dcx') {
				data = this._getColorThemeData(libResult);
			}

			if (libResult.type === 'application/vnd.adobe.element.color+dcx') {
				data = this._getColorData(libResult);

				// generate a name for this color if one doesn't exist
				if (data && libResult.name.length === 0) {
					// $FlowFixMe - doesn't like derefercing data.value.color
					libResult.name = `#${Number(data.value.color.r).toString(16)}${Number(data.value.color.g).toString(
						16,
					)}${Number(data.value.color.b).toString(16)}`.toUpperCase();
				}
			}

			// leave parentId undefined
			let parentId;
			let parentUrl;
			let openUrl;

			if (!isLibrary) {
				parentId = libResult.library.id;
				openUrl = `libraries/${parentId}/elements/${libResult.id}`;
				parentUrl = `libraries/${parentId}`;
			} else {
				openUrl = `libraries/${libResult.id}`;
				data = libraryMosaicService.getLocalLibraryMosaicTiles(libResult);
			}

			const resultItem = {
				id: libResult.id,
				assetType: isLibrary ? 'library' : 'libraryElement',
				attributes: {
					name: libResult.name,
					modificationDate: libResult.modified_date,
					creationDate: libResult.created_date,
					fileSize: libResult.size,
					isLocal: true,
					openUrl,
					mimeType: isLibrary ? LIBRARY_MIMETYPE_MAP.LIBRARY : libResult.type,
					imageRenditionUrl: isLibrary ? undefined : this._findRendition(libResult),
					assetCategory: 'libraries',
					parentUrl,
					parentId,
					ownershipType: ownershipMapper[isLibrary ? libResult.ownership : libResult.library.ownership],
					data,
					teamAsset: this._isTeamAsset(libResult),
				},
			};
			return Object.freeze(resultItem);
		});
		return {
			resultItems,
			serverResult: libraryResults.serverResult,
			totalResults: libraryResults.response._totalCount
				? libraryResults.response._totalCount
				: libraryResults.response._page.count,
			nextPageReference: libraryResults.response._page.next,
			duration: 0,
		};
	}

	_getColorData(libResult: Record<string, any>): Data | typeof undefined {
		// leave data undefined
		let data: Data;
		// find the color representation
		const rep = libResult.representations.find(
			(element) => element.type === 'application/vnd.adobe.color+json' && element['color#data'].mode === 'RGB',
		);

		if (rep && rep['color#data'] && rep['color#data'].value) {
			const colorValue: ColorTile = {
				type: 'color',
				color: {
					// round the color values to the nearest integer
					r: Math.round(rep['color#data'].value.r),
					g: Math.round(rep['color#data'].value.g),
					b: Math.round(rep['color#data'].value.b),
				},
			};
			data = {
				type: 'color',
				value: colorValue,
			};
			return data;
		}

		return undefined;
	}

	_findRGB(swatch: Record<string, any>): RGBColor {
		/* a swatch is an array of color objects
    	we need to find the object whose mode is "RGB"
    [
    	{
    		"mode": "RGB",
    		"value": {
    			"r": 50,
    			"b": 50,
    			"g": 50
    		}
    	}
    ]
    */
		const rgbColor = swatch.find((element) => element.mode && element.mode === 'RGB');
		return rgbColor.value;
	}

	_getColorThemeData(libResult: Record<string, any>): Data | typeof undefined {
		const rep = libResult.representations.find(
			(element) => element.type === 'application/vnd.adobe.colortheme+json',
		);
		let data: Data;

		if (rep['colortheme#data'] && rep['colortheme#data'].swatches) {
			// extract the colors out of the swatches
			const colors: Array<RGBColor> = rep['colortheme#data'].swatches.map((swatch) => {
				const rgb: RGBColor = this._findRGB(swatch);

				return rgb;
			});
			const colorTheme: ColorThemeTile = {
				type: 'colorTheme',
				colors,
			};
			data = {
				type: 'colorTheme',
				value: colorTheme,
			};
			return data;
		}

		return undefined;
	}

	// must be passed a library element
	_findRendition(libraryResult: Record<string, any>): string | typeof undefined {
		let rendition: Record<string, any>;

		switch (libraryResult.type) {
			case 'application/vnd.adobe.element.video+dcx':
				rendition = libraryResult.representations.reduce((acc, curr) => {
					let r;

					if (
						(curr.type === 'image/jpeg' || curr.type === 'image/png') &&
						((curr.width >= LibrarySearch.VIDEO_RENDITION_SIZE - LibrarySearch.VIDEO_SIZE_SLOP &&
							curr.width <= LibrarySearch.VIDEO_RENDITION_SIZE + LibrarySearch.VIDEO_SIZE_SLOP) ||
							(curr.height >= LibrarySearch.VIDEO_RENDITION_SIZE - LibrarySearch.VIDEO_SIZE_SLOP &&
								curr.height <= LibrarySearch.VIDEO_RENDITION_SIZE + LibrarySearch.VIDEO_SIZE_SLOP))
					) {
						r = curr;
					}

					return r;
				}, undefined);
				break;

			case 'application/vnd.adobe.element.characterstyle+dcx':
			// fall through
			case 'application/vnd.adobe.element.paragraphstyle+dcx':
				rendition = libraryResult.representations.reduce((acc, curr) => {
					let r;

					if (curr.type === 'image/png') {
						r = curr;
					}

					return r;
				}, undefined);
				break;

			default:
				rendition = libraryResult.representations.reduce((acc, curr) => {
					let r;

					if (
						curr.relationship === 'rendition' &&
						(curr.width === LibrarySearch.IMAGE_RENDITION_SIZE ||
							curr.height === LibrarySearch.IMAGE_RENDITION_SIZE)
					) {
						r = curr;
					}

					return r;
				}, undefined);

				if (!rendition && libraryResult.thumbnail_cache) {
					// nothing matched at the rendition level, try the thumbnail cache
					const cacheKey = Object.keys(libraryResult.thumbnail_cache).reduce((acc, curr) => {
						// leave undefined
						// @ts-ignore
						let thumbSize = acc;
						const size = parseInt(curr, 10);

						if (!isNaN(size)) {
							// we have a number
							const currDist = Math.abs(LibrarySearch.IMAGE_RENDITION_SIZE - size);

							if (acc) {
								const accDist = Math.abs(LibrarySearch.IMAGE_RENDITION_SIZE - parseInt(acc, 10));

								if (currDist < accDist) {
									// @ts-ignore
									thumbSize = size;
								}
							} else {
								// @ts-ignore
								thumbSize = size;
							}
						}

						return thumbSize;
					}, undefined);

					if (cacheKey) {
						// put the thumbnail cache item in the correct form
						rendition = {
							path: libraryResult.thumbnail_cache[cacheKey],
						};
					}
				}
		}

		let renditionPath;

		if (rendition) {
			renditionPath = `file://${rendition.path}`;
		} else {
			this.logger.log('Library Search', 'no rendition found');
		}

		return renditionPath;
	}
}
LibrarySearch.VIDEO_RENDITION_SIZE = 426;
LibrarySearch.VIDEO_SIZE_SLOP = 5;
LibrarySearch.IMAGE_RENDITION_SIZE = 500;
